import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Loader2,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Target,
  Sparkles,
  BarChart3,
  Printer,
  Share2,
  Copy,
  Check,
  FileText,
  Calendar,
  Mail
} from 'lucide-react';

const CVReviewSharePage = () => {
  const { shareToken } = useParams();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    fetchReview();
  }, [shareToken]);

  const fetchReview = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/cv-review/share/${shareToken}`
      );
      
      if (!response.ok) {
        throw new Error('Review not found or link has expired');
      }
      
      const data = await response.json();
      setReview(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCopyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-500';
    if (score >= 60) return 'text-amber-500';
    return 'text-red-500';
  };

  const getScoreGradient = (score) => {
    if (score >= 80) return 'from-emerald-500 to-teal-500';
    if (score >= 60) return 'from-amber-500 to-orange-500';
    return 'from-red-500 to-rose-500';
  };

  const getProgressColor = (score) => {
    if (score >= 80) return 'bg-gradient-to-r from-emerald-500 to-teal-500';
    if (score >= 60) return 'bg-gradient-to-r from-amber-500 to-orange-500';
    return 'bg-gradient-to-r from-red-500 to-rose-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary-500 animate-spin mx-auto mb-4" />
          <p className="text-neutral-600">Loading CV Review...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <Card className="max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-neutral-900 mb-2">Review Not Found</h2>
            <p className="text-neutral-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Print Styles */}
      <style>
        {`
          @media print {
            .no-print { display: none !important; }
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .print-break { page-break-before: always; }
          }
        `}
      </style>

      {/* Header with Actions */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10 no-print">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-violet-500 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-neutral-900">CV Review Report</h1>
              <p className="text-sm text-neutral-500">{review?.filename || 'CV Analysis'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyLink}
              className="gap-2"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
            <Button
              size="sm"
              onClick={handlePrint}
              className="gap-2 bg-gradient-to-r from-primary-600 to-violet-600"
            >
              <Printer className="w-4 h-4" />
              Print Report
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8" ref={printRef}>
        {/* Header Info */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">CV Analysis Report</h1>
          <div className="flex items-center justify-center gap-4 text-sm text-neutral-600">
            {review?.email && (
              <span className="flex items-center gap-1">
                <Mail className="w-4 h-4" />
                {review.email}
              </span>
            )}
            {review?.created_at && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(review.created_at).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </span>
            )}
          </div>
        </div>

        {/* Overall Score Card */}
        <Card className="overflow-hidden border-0 shadow-xl mb-6">
          <div className={`bg-gradient-to-r ${getScoreGradient(review?.overall_score || 0)} p-1`}>
            <div className="bg-white rounded-lg">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  {/* Score Circle */}
                  <div className="relative">
                    <div className="w-40 h-40 rounded-full bg-gradient-to-br from-neutral-100 to-neutral-50 flex items-center justify-center shadow-inner">
                      <div className={`text-6xl font-bold bg-gradient-to-r ${getScoreGradient(review?.overall_score || 0)} bg-clip-text text-transparent`}>
                        {review?.overall_score || 0}
                      </div>
                    </div>
                    <div className={`absolute -bottom-2 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-white text-sm font-medium bg-gradient-to-r ${getScoreGradient(review?.overall_score || 0)}`}>
                      out of 100
                    </div>
                  </div>
                  
                  {/* Score Details */}
                  <div className="flex-1 text-center md:text-left">
                    <h2 className="text-2xl font-bold text-neutral-900 mb-2">Overall ATS Score</h2>
                    <p className="text-neutral-600 mb-4">
                      {review?.overall_score >= 80 
                        ? "Excellent! This CV is well-optimized for ATS systems."
                        : review?.overall_score >= 60 
                        ? "Good start! With a few improvements, this CV will perform better."
                        : "This CV needs significant improvements to pass ATS filters."}
                    </p>
                    <Badge 
                      className={`px-4 py-1.5 text-sm ${
                        review?.overall_score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                        review?.overall_score >= 60 ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}
                    >
                      {review?.overall_score >= 80 ? 'Excellent' :
                       review?.overall_score >= 60 ? 'Needs Improvement' :
                       'Needs Work'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </div>
          </div>
        </Card>

        {/* Section Scores */}
        <Card className="border-0 shadow-xl mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary-600" />
              Section Breakdown
            </CardTitle>
            <CardDescription>Detailed analysis of each CV section</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5 pt-4">
            {review?.sections?.map((section, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-neutral-900">{section.name}</span>
                  <span className={`font-bold ${getScoreColor(section.score)}`}>
                    {section.score}/100
                  </span>
                </div>
                <div className="w-full bg-neutral-100 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-3 rounded-full ${getProgressColor(section.score)} transition-all duration-500`}
                    style={{ width: `${section.score}%` }}
                  />
                </div>
                <p className="text-sm text-neutral-600">{section.feedback}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Keyword Analysis */}
        {review?.keywords && (
          <Card className="border-0 shadow-xl mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary-600" />
                Keyword Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-emerald-50 rounded-xl p-5">
                  <h4 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    Keywords Found ({review.keywords.found?.length || 0})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {review.keywords.found?.map((kw, i) => (
                      <Badge key={i} className="bg-emerald-100 text-emerald-700 border-0">
                        {kw}
                      </Badge>
                    ))}
                    {(!review.keywords.found || review.keywords.found.length === 0) && (
                      <span className="text-sm text-neutral-500">No keywords identified</span>
                    )}
                  </div>
                </div>
                <div className="bg-amber-50 rounded-xl p-5">
                  <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Missing Keywords ({review.keywords.missing?.length || 0})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {review.keywords.missing?.map((kw, i) => (
                      <Badge key={i} variant="outline" className="border-amber-300 text-amber-700 bg-white">
                        {kw}
                      </Badge>
                    ))}
                    {(!review.keywords.missing || review.keywords.missing.length === 0) && (
                      <span className="text-sm text-neutral-500">No missing keywords identified</span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recommendations */}
        {review?.recommendations && review.recommendations.length > 0 && (
          <Card className="border-0 shadow-xl overflow-hidden mb-6 print-break">
            <div className="bg-gradient-to-r from-primary-500 to-violet-500 p-4">
              <CardTitle className="text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Recommendations to Improve Score
              </CardTitle>
            </div>
            <CardContent className="pt-6">
              <ul className="space-y-4">
                {review.recommendations.map((rec, index) => (
                  <li key={index} className="flex gap-4 items-start">
                    <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-violet-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary-500/20">
                      <span className="text-sm font-bold text-white">{index + 1}</span>
                    </div>
                    <p className="text-neutral-700 pt-1">{rec}</p>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Formatting Issues */}
        {review?.formatting_issues && review.formatting_issues.length > 0 && (
          <Card className="border-0 shadow-xl border-l-4 border-l-amber-500 mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-5 h-5" />
                Formatting Issues to Fix
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {review.formatting_issues.map((issue, index) => (
                  <li key={index} className="flex items-start gap-3 text-neutral-700">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    {issue}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-neutral-500 mt-8 pt-8 border-t">
          <p>Generated by RecruitNearMe CV Review Service</p>
          <p className="mt-1">Visit recruitnearme.co.uk for more career services</p>
        </div>
      </div>
    </div>
  );
};

export default CVReviewSharePage;
